/**
 * Detect signals that the AI is failing the user and a human should take
 * over. The widget uses these to PROACTIVELY offer handoff (with a
 * one-click Yes/No prompt) instead of forcing the user to guess a magic
 * phrase like "pásame con un asesor".
 *
 * Three independent triggers, any of which is enough:
 *
 *   1. Frustration phrase in the user's latest message
 *      ("no funciona", "ya hice eso", "no me sirve", "ayuda por favor",
 *       "cómo los contacto", "a quién le reclamo", etc.).
 *
 *   2. AI refusal in the bot's latest answer
 *      ("lamentablemente no puedo", "no tengo esa información",
 *       "no puedo ayudarte con eso", etc.).
 *
 *   3. Repeated back-and-forth without resolution — the customer has sent
 *      a threshold number of messages (default 3) and at least one bot
 *      reply looks unhelpful OR low-confidence.
 *
 * All signals are language-aware (Spanish + English) and intentionally
 * regex-based so they are deterministic, testable, and zero-latency.
 */

const FRUSTRATION_RE =
  /\b(no\s+(?:funciona|sirve|anda|jala|me\s+sirve|entiend[oe]|puedo)|sigue\s+sin\s+(?:funcionar|servir|andar)|ya\s+(?:hice|intent[eé]|prob[eé])|es\s+lo\s+mismo|nada\s+(?:funciona|sirve)|no\s+pasa\s+nada|no\s+hay\s+caso|no\s+me\s+ayuda|esto\s+no|est[aá]\s+mal|est[aá]\s+roto|c[oó]mo\s+(?:los?\s+)?contact(?:o|arlos|ar)|a\s+qui[eé]n\s+(?:le\s+)?reclamo|qu[eé]\s+hago|ay[uú]dame|ay[uú]denme|necesito\s+ayuda|urgente|me\s+urge|est[aá]s?\s+mal|eres\s+(?:un\s+)?in[uú]til|no\s+me\s+est[aá]s?\s+ayudando|not\s+working|doesn't\s+work|already\s+tried|this\s+is\s+broken|help\s+me|i\s+need\s+help|urgent|useless|you're\s+not\s+helping|how\s+do\s+i\s+contact|who\s+do\s+i\s+contact)\b/i;

const AI_REFUSAL_RE =
  /\b(lamentablemente\s+no|no\s+puedo\s+(?:ayudarte|conectar|proporcionar|darte|brindarte|responder|acceder|resolver)|no\s+tengo\s+(?:esa|la)\s+(?:información|respuesta|capacidad|posibilidad)|no\s+dispongo|no\s+est[aá]\s+en\s+(?:mi|la)\s+(?:base|documentaci[oó]n)|fuera\s+de\s+mi\s+(?:alcance|capacidad)|te\s+recomiendo\s+(?:que\s+)?(?:revises|contactes|consultes)|i\s+(?:can(?:no|')t|am\s+unable)\s+(?:help|connect|provide|answer)|i\s+don'?t\s+have\s+(?:that|the)\s+information|outside\s+(?:my|the)\s+scope|i\s+recommend\s+(?:you\s+)?(?:contact|check))\b/i;

export function hasFrustrationSignal(userMessage: string): boolean {
  return FRUSTRATION_RE.test(userMessage);
}

export function hasAiRefusalSignal(aiAnswer: string): boolean {
  return AI_REFUSAL_RE.test(aiAnswer);
}

export interface EscalationDecisionInput {
  readonly userMessage: string;
  readonly lastAiAnswer: string;
  readonly customerTurnCount: number;
  readonly lastAiConfidence: number | null;
  /** Minimum customer turns before "repeated back-and-forth" fires. */
  readonly repeatedTurnThreshold?: number;
  /** Below this confidence the AI is treated as unhelpful. */
  readonly lowConfidenceCeiling?: number;
}

export interface EscalationDecision {
  readonly shouldOffer: boolean;
  /** Debug string — which rule fired. Empty when nothing triggered. */
  readonly reason: string;
}

/**
 * Decide whether to OFFER handoff (a one-click Yes/No prompt in the chat
 * thread). Never force — always ask the user. The explicit-request regex
 * is still handled separately and DOES force handoff (the user was
 * unambiguous).
 */
export function decideOfferHandoff(
  input: EscalationDecisionInput,
): EscalationDecision {
  const threshold = input.repeatedTurnThreshold ?? 3;
  const lowConf = input.lowConfidenceCeiling ?? 0.55;

  if (hasFrustrationSignal(input.userMessage)) {
    return { shouldOffer: true, reason: 'frustration' };
  }
  if (hasAiRefusalSignal(input.lastAiAnswer)) {
    return { shouldOffer: true, reason: 'ai_refusal' };
  }
  if (
    input.customerTurnCount >= threshold &&
    input.lastAiConfidence !== null &&
    input.lastAiConfidence < lowConf
  ) {
    return { shouldOffer: true, reason: 'low_confidence_repeated' };
  }
  return { shouldOffer: false, reason: '' };
}
